import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Server, Save, ShieldCheck, Info, Zap } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Platform-wide SMTP/IMAP defaults. Any tenant that doesn't override in their
 * own Email config inherits these credentials — so tenants get a working
 * outbound sender the moment they onboard.
 *
 * Multi-tenant safety: each tenant is issued a unique inbound alias of the
 * form `<user>+<slug>@<domain>`. The IMAP poller reads the To: header and
 * routes each message to the matching tenant. Cross-tenant leakage is
 * impossible because every case carries `tenant_id`.
 */
export default function PlatformSmtp() {
  const { isSuperAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});
  const [testing, setTesting] = useState(false);
  const [tr, setTr] = useState(null);
  const test = async () => {
    setTesting(true); setTr(null);
    try {
      const r = await api.post("/platform/smtp/test");
      setTr(r.data);
      (r.data.ok ? toast.success : toast.error)(
        `IMAP ${r.data.imap?.ok ? "✓" : "✗"} · SMTP ${r.data.smtp?.ok ? "✓" : "✗"}`,
        { description: `${r.data.imap?.detail}\n${r.data.smtp?.detail}`, duration: 6000 }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/smtp");
      setCfg(data);
      setF({
        imap_host: data.imap_host || "",
        imap_port: data.imap_port || 993,
        imap_user: data.imap_user || "",
        imap_password: "",
        imap_mailbox: data.imap_mailbox || "INBOX",
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || "",
        smtp_password: "",
        from_address: data.from_address || "",
        enabled: !!data.enabled,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load platform SMTP");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/smtp", f);
      setCfg(data);
      toast.success("Platform default SMTP saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  if (!isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center p-8" data-testid="smtp-forbidden">
        <div className="max-w-md text-center">
          <ShieldCheck className="w-10 h-10 text-[#FFC542] mx-auto mb-3" />
          <div className="font-display font-black text-2xl mb-2">Super-admin only</div>
          <p className="text-sm text-[#8E9196]">Platform-wide SMTP defaults are managed by the platform owner.</p>
        </div>
      </div>
    );
  }

  const baseLocal = f.smtp_user && f.smtp_user.includes("@") ? f.smtp_user.split("@")[0] : "soc";
  const baseDomain = f.smtp_user && f.smtp_user.includes("@") ? f.smtp_user.split("@")[1] : "forensiq.io";

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="platform-smtp-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="w-5 h-5 text-[#B57BFF]" strokeWidth={1.75} />
          <h1 className="font-display font-black text-3xl tracking-tight">PLATFORM MAIL DEFAULTS</h1>
        </div>
        <p className="text-sm text-[#8E9196] max-w-3xl mb-6">
          One SMTP + IMAP shared by every tenant that hasn&apos;t configured their own. New tenants get a working
          outbound sender the moment you onboard them. Any tenant may override with their own credentials in
          <span className="text-white"> /email-investigator → Configure mailbox</span>.
        </p>

        {/* Safety explainer */}
        <div className="mb-6 border border-[#00F0FF]/30 bg-[#00F0FF]/[0.04] rounded-sm p-4" data-testid="smtp-safety-callout">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-[#00F0FF] mt-0.5" />
            <div className="text-xs text-[#8E9196]">
              <div className="text-white font-medium mb-1">How cross-tenant safety works with a shared inbox</div>
              Each tenant gets a unique inbound alias like
              <span className="ml-1 font-mono text-[#00F0FF]">{baseLocal}+&lt;tenant-slug&gt;@{baseDomain}</span>. When a mail lands on the shared inbox, the poller extracts the
              <span className="text-[#00F0FF]"> +tag </span> from the <span className="font-mono">To:</span> header and pins the case to that tenant. Every DB record carries
              <span className="font-mono text-[#00F0FF]"> tenant_id</span>, so one company can never see another&apos;s cases.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-xs text-[#52525B] font-mono">Loading…</div>
        ) : (
          <div className="border border-white/10 bg-[#0a0a0a] rounded-sm p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-2 flex items-center gap-1">
                  <Server className="w-3 h-3" /> Inbound (IMAP)
                </div>
                <Input label="Host" value={f.imap_host} onChange={(v) => setF({ ...f, imap_host: v })} testid="platform-imap-host" placeholder="imap.gmail.com" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input label="Port" type="number" value={f.imap_port} onChange={(v) => setF({ ...f, imap_port: parseInt(v) || 993 })} testid="platform-imap-port" />
                  <Input label="Mailbox" value={f.imap_mailbox} onChange={(v) => setF({ ...f, imap_mailbox: v })} testid="platform-imap-mailbox" placeholder="INBOX" />
                </div>
                <Input className="mt-2" label="Username" value={f.imap_user} onChange={(v) => setF({ ...f, imap_user: v })} testid="platform-imap-user" placeholder="noreply@forensiq.io" />
                <Input className="mt-2" label="Password / app password" type="password" value={f.imap_password}
                       onChange={(v) => setF({ ...f, imap_password: v })} testid="platform-imap-password"
                       placeholder={cfg?.imap_password_set ? "•••• (unchanged)" : "app password"} />
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-2 flex items-center gap-1">
                  <Server className="w-3 h-3" /> Outbound (SMTP)
                </div>
                <Input label="Host" value={f.smtp_host} onChange={(v) => setF({ ...f, smtp_host: v })} testid="platform-smtp-host" placeholder="smtp.gmail.com" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input label="Port" type="number" value={f.smtp_port} onChange={(v) => setF({ ...f, smtp_port: parseInt(v) || 587 })} testid="platform-smtp-port" />
                  <Input label="From address" value={f.from_address} onChange={(v) => setF({ ...f, from_address: v })} testid="platform-from-address" placeholder="noreply@forensiq.io" />
                </div>
                <Input className="mt-2" label="Username" value={f.smtp_user} onChange={(v) => setF({ ...f, smtp_user: v })} testid="platform-smtp-user" />
                <Input className="mt-2" label="Password / app password" type="password" value={f.smtp_password}
                       onChange={(v) => setF({ ...f, smtp_password: v })} testid="platform-smtp-password"
                       placeholder={cfg?.smtp_password_set ? "•••• (unchanged)" : "app password"} />
              </div>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
              <label className="flex items-center gap-2 text-xs text-[#8E9196]">
                <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="platform-enable-poll" />
                Enable platform inbox polling for shared-inbox tenants (every 60s)
              </label>
              <div className="flex items-center gap-2">
                <button onClick={save} disabled={saving} data-testid="platform-smtp-save"
                        className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-40">
                  <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save platform default"}
                </button>
                <button onClick={test} disabled={testing} data-testid="platform-smtp-test"
                        className="inline-flex items-center gap-2 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-40">
                  <Zap className="w-3.5 h-3.5" /> {testing ? "Testing…" : "Test connection"}
                </button>
                {tr && (
                  <div className="text-xs font-mono" data-testid="platform-smtp-test-result">
                    <span className={tr.imap?.ok ? "text-[#34C759]" : "text-[#FF3B30]"}>IMAP {tr.imap?.ok ? "✓" : "✗"}</span>
                    <span className="mx-1 text-[#52525B]">·</span>
                    <span className={tr.smtp?.ok ? "text-[#34C759]" : "text-[#FF3B30]"}>SMTP {tr.smtp?.ok ? "✓" : "✗"}</span>
                  </div>
                )}
              </div>
            </div>

            {cfg?.updated_at && (
              <div className="mt-3 text-[10px] font-mono text-[#52525B]">
                Last saved {new Date(cfg.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", placeholder, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type} data-testid={testid} placeholder={placeholder}
             className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white placeholder:text-[#52525B]" />
    </div>
  );
}
